import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Copy, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const RandomStringTool = () => {
  const [length, setLength] = useState(16);
  const [count, setCount] = useState(1);
  const [options, setOptions] = useState({
    uppercase: true,
    lowercase: true,
    numbers: true,
    symbols: false,
  });
  const [results, setResults] = useState([]);

  const generate = () => {
    let chars = '';
    if (options.uppercase) chars += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (options.lowercase) chars += 'abcdefghijklmnopqrstuvwxyz';
    if (options.numbers) chars += '0123456789';
    if (options.symbols) chars += '!@#$%^&*()_+-=[]{}|;:,.<>?';

    if (!chars) {
      toast.error('Select at least one character type');
      return;
    }

    const strings = [];
    for (let i = 0; i < count; i++) {
      let str = '';
      const array = new Uint32Array(length);
      crypto.getRandomValues(array);
      for (let j = 0; j < length; j++) {
        str += chars[array[j] % chars.length];
      }
      strings.push(str);
    }

    setResults(strings);
  };

  const copyResults = () => {
    navigator.clipboard.writeText(results.join('\n'));
    toast.success('Strings copied!');
  };

  return (
    <div className="space-y-6" data-testid="random-string-tool">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Length</label>
              <Input
                type="number"
                min={1}
                max={256}
                value={length}
                onChange={(e) => setLength(Math.min(256, parseInt(e.target.value) || 1))}
              />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Count</label>
              <Input
                type="number"
                min={1}
                max={100}
                value={count}
                onChange={(e) => setCount(Math.min(100, parseInt(e.target.value) || 1))}
              />
            </div>
          </div>

          <div className="p-4 rounded-xl bg-muted/30 border border-border">
            <h3 className="font-semibold mb-3">Character Types</h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'uppercase', label: 'Uppercase (A-Z)' },
                { key: 'lowercase', label: 'Lowercase (a-z)' },
                { key: 'numbers', label: 'Numbers (0-9)' },
                { key: 'symbols', label: 'Symbols (!@#$...)' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options[key]}
                    onChange={(e) => setOptions(prev => ({ ...prev, [key]: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>

          <Button onClick={generate} className="w-full glow-blue">
            <Sparkles className="w-4 h-4 mr-2" /> Generate Random Strings
          </Button>
        </div>

        <div>
          <label className="text-sm text-muted-foreground mb-2 block">
            Generated Strings ({results.length})
          </label>
          <Textarea
            value={results.join('\n')}
            readOnly
            className="min-h-[250px] font-mono text-sm bg-muted/50"
            placeholder="Generated strings will appear here..."
          />
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={copyResults} disabled={results.length === 0}>
          <Copy className="w-4 h-4 mr-2" /> Copy All
        </Button>
        <Button onClick={() => setResults([])} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Clear
        </Button>
      </div>
    </div>
  );
};

export default RandomStringTool;
